import { t } from 'i18next';
import type { I18nKey } from './typings';
import { JobFrequency as Freq } from '@/api/generator';
import { isNumber } from 'lodash';

type CompareFunction<T> = (a: T, b: T) => number;

export function idEqual(a: { id?: string | number }, b: { id?: string | number }) {
  return a?.id === b?.id;
}

export function optionalNumberCompare(a?: number | null, b?: number | null) {
  if (isNumber(a) && !(isNumber(b) && b >= a)) {
    return 1;
  } else if (isNumber(b) && !(isNumber(a) && a >= b)) {
    return -1;
  } else {
    return 0;
  }
}

export function alphabeticalStringCompare(a: string, b: string) {
  return a.localeCompare(b);
}

/**
 * NOTE: items not in the order array are sorted to the beginning of the array. (index -1)
 * This behavior can produce unexpected results if unaware of the behavior.
 *
 * Expected behavior for when items are not in the order array:
 *  - Throw an error or exception
 *  - Sort the item not in array to the end of the array
 *  - have an explicit expectation for the behavior
 */
export function orderCompare<T>(order: Array<T>) {
  return (a: T, b: T) => {
    const [aIndex, bIndex] = [a, b].map((value) => order.indexOf(value));

    if (aIndex > bIndex) return 1;
    else if (aIndex < bIndex) return -1;
    else return 0;
  };
}

/**
 * Creates a compare function that applies multiple comparators in sequence.
 * Returns the result of the first comparator that produces a non-zero value.
 * Useful for multi-field sorting where you want primary, secondary, etc. sort keys.
 *
 * @example
 * Sort by category first, then by subCategory if categories are equal
 * sortRaw: cascadedCompareFunction<ItemDto>([
 *   (a, b) => localizedEnumCompare('CategoryEnum')(a.category, b.category),
 *   (a, b) => localizedEnumCompare('SubCategoryEnum')(a.subCategory, b.subCategory),
 * ]),
 */
export function cascadedCompareFunction<T>(compareFunctions: CompareFunction<T>[]) {
  function compareFunction(a: T, b: T) {
    for (const compare of compareFunctions) {
      const result = compare(a, b);
      if (result != 0) return result;
    }

    return 0;
  }

  return compareFunction;
}

/**
 * should strongly consider using localeCompare() instead of the if statements.
 *     const valueA = t(`${enumPrefix}.${a}` as I18nKey).toLowerCase();
 *     const valueB = t(`${enumPrefix}.${b}` as I18nKey).toLowerCase();
 *     return valueA.localeCompare(valueB) as -1 | 0 | 1;
 */
export function localizedEnumCompare(enumPrefix: string) {
  return (a: string, b: string) => {
    const valueA = t(`${enumPrefix}.${a}` as I18nKey).toLowerCase();
    const valueB = t(`${enumPrefix}.${b}` as I18nKey).toLowerCase();
    return alphabeticalStringCompare(valueA, valueB);
  };
}

// This function needs to be refactored. Its name does not describe the behavior.
// This violates the comparison contract: compare(a, a) should always be 0.
export function withLastItemCompare<T>(
  compareFunction: CompareFunction<T>,
  isLastItem: (item: T) => boolean,
) {
  return (a: T, b: T) => {
    if (isLastItem(a)) {
      return 1;
    } else if (isLastItem(b)) {
      return -1;
    } else {
      return compareFunction(a, b);
    }
  };
}

const frequencyOrder = [Freq.None, Freq.Daily, Freq.Weekly, Freq.Monthly, Freq.Yearly];
const frequencyCompare = orderCompare(frequencyOrder);

export function compareFrequencyType(a: Freq, b: Freq) {
  return frequencyCompare(a, b);
}
